// Iter38 — Defect Pareto inline chart for sorting results.
//
// Recharts horizontal bar — defect types on Y, count on X, sorted desc.
// Aggregates across all sorting_results passed in. Top defect amber, rest blue.
import React, { useMemo } from "react";
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Cell, LabelList, Tooltip } from "recharts";
import { useT } from "../lib/i18n";

const MAX_BARS = 6;
const TOP_COLOR = "#f3b14a";
const REST_COLOR = "#185FA5";

export const DefectPareto = ({ results = [], testId = "defect-pareto" }) => {
  const { t } = useT();

  const data = useMemo(() => {
    const counts = {};
    for (const r of results) {
      const types = r.defect_types || [];
      for (const ty of types) counts[ty] = (counts[ty] || 0) + 1;
    }
    const arr = Object.entries(counts)
      .map(([type, count]) => ({ type, count }))
      .sort((a, b) => b.count - a.count);
    const top = arr.slice(0, MAX_BARS);
    const overflow = arr.length - top.length;
    if (overflow > 0) top.push({ type: `+${overflow} more`, count: arr.slice(MAX_BARS).reduce((s, x) => s + x.count, 0), __more: true });
    return top;
  }, [results]);

  if (!data.length) return null;

  const label = (k, fallback) => {
    const v = t(k);
    return v === k ? fallback : v;
  };

  return (
    <div
      className="rounded-md p-3"
      style={{ background: "var(--bg-surface, #0d0f18)", border: "1px solid rgba(255,255,255,0.10)" }}
      data-testid={testId}
    >
      <div
        className="mb-2 flex items-center justify-between"
        style={{ fontFamily: "Syne, sans-serif", fontWeight: 700, fontSize: 12, color: "var(--text, #f0f2f8)" }}
      >
        {label("sorting.pareto.title", "Defect Pareto")}
        <span style={{ fontFamily: "DM Mono, monospace", fontSize: 9, color: "#9198b0", letterSpacing: "0.5px" }} data-testid={`${testId}-source-count`}>
          {label("sorting.pareto.from", "from")} {results.length} {label("sorting.pareto.results", "results")}
        </span>
      </div>
      <div style={{ width: "100%", height: Math.max(120, data.length * 28 + 12) }}>
        <ResponsiveContainer>
          <BarChart data={data} layout="vertical" margin={{ left: 4, right: 24, top: 4, bottom: 4 }}>
            <XAxis type="number" hide />
            <YAxis
              dataKey="type"
              type="category"
              tick={{ fill: "var(--text-dim, #c5cad8)", fontFamily: "DM Mono, monospace", fontSize: 10 }}
              width={120}
              tickLine={false}
              axisLine={false}
            />
            <Tooltip
              cursor={{ fill: "rgba(255,255,255,0.04)" }}
              contentStyle={{
                background: "var(--bg2, #0a0c12)",
                border: "1px solid rgba(255,255,255,0.12)",
                fontFamily: "DM Mono, monospace",
                fontSize: 11,
              }}
              labelStyle={{ color: "var(--text, #f0f2f8)" }}
            />
            <Bar dataKey="count" radius={[0, 2, 2, 0]}>
              {data.map((d, i) => (
                <Cell key={d.type} fill={i === 0 && !d.__more ? TOP_COLOR : REST_COLOR} />
              ))}
              <LabelList dataKey="count" position="right" fill="var(--text, #f0f2f8)" style={{ fontFamily: "DM Mono, monospace", fontSize: 10 }} />
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};
